import {
  COMPLIANCE_RECORD_STATUSES,
  COMPLIANCE_RECORD_STATUS_LABELS,
  type ComplianceRecordStatus,
} from '@sitewise/shared';
import {
  Button,
  ComplianceBadge,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  Skeleton,
  cn,
} from '@sitewise/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Filter, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  listComplianceRecords,
  type RecordWithDeviceType,
} from '../../../api/compliance.js';

import { formatDate, formatRelativeDays } from './lib.js';
import { LogComplianceSheet } from './log-sheet.js';
import { SkipSnoozeDialog } from './skip-snooze-dialog.js';

interface FilterState {
  q: string;
  status: ComplianceRecordStatus[];
}

export function ComplianceRecordsPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const initialStatus = parseList(params.get('status')) as ComplianceRecordStatus[];

  const [filters, setFilters] = useState<FilterState>({
    q: params.get('q') ?? '',
    status: initialStatus,
  });

  const [logRecord, setLogRecord] = useState<RecordWithDeviceType | null>(null);
  const [skipRecord, setSkipRecord] = useState<{ record: RecordWithDeviceType; kind: 'skip' | 'snooze' } | null>(null);

  const queryKey = useMemo(() => ['compliance', 'records', filters], [filters]);
  const recQuery = useQuery({
    queryKey,
    queryFn: () =>
      listComplianceRecords({
        q: filters.q || undefined,
        status: filters.status.length > 0 ? filters.status : undefined,
      }),
    placeholderData: (prev) => prev,
  });

  function applyFilter(next: FilterState) {
    setFilters(next);
    const sp = new URLSearchParams();
    if (next.q) sp.set('q', next.q);
    if (next.status.length > 0) sp.set('status', next.status.join(','));
    setParams(sp, { replace: true });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
          <Input
            placeholder="Search device or building…"
            value={filters.q}
            onChange={(e) => applyFilter({ ...filters, q: e.target.value })}
            className="w-72 pl-8"
          />
        </div>
        <StatusFilter
          value={filters.status}
          onChange={(s) => applyFilter({ ...filters, status: s })}
        />
      </div>

      {recQuery.isError ? (
        <ErrorState description="Could not load compliance records." />
      ) : recQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (recQuery.data?.records.length ?? 0) === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No compliance records"
          description="Add devices in the Devices tab — records appear automatically as inspections are scheduled."
        />
      ) : (
        <DataTable<RecordWithDeviceType>
          rowKey={(r) => r.id}
          rows={recQuery.data!.records}
          columns={[
            {
              id: 'device',
              header: 'Device',
              description: 'The piece of equipment this compliance item is for.',
              cell: (r) => (
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.device.label}</div>
                  <div className="truncate text-xs text-fg-muted">{r.deviceType.name}</div>
                </div>
              ),
            },
            {
              id: 'dueAt',
              header: 'Due',
              description: 'When the next inspection or service is due.',
              cell: (r) => (
                <div className="whitespace-nowrap">
                  <div className="tabular-nums">{formatDate(r.dueAt)}</div>
                  <div className="text-xs text-fg-muted">{formatRelativeDays(r.dueAt)}</div>
                </div>
              ),
            },
            {
              id: 'status',
              header: 'Status',
              description: 'Whether the item is upcoming, due soon, overdue, or complete.',
              cell: (r) => (
                <ComplianceBadge tone={r.tone} withDot>
                  {COMPLIANCE_RECORD_STATUS_LABELS[r.status]}
                </ComplianceBadge>
              ),
            },
            {
              id: 'actions',
              header: '',
              cell: (r) => (
                <div className="flex justify-end gap-1">
                  {(r.status === 'upcoming' ||
                    r.status === 'due_soon' ||
                    r.status === 'overdue') && (
                    <>
                      <Button size="sm" onClick={() => setLogRecord(r)}>
                        Log
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSkipRecord({ record: r, kind: 'snooze' })}
                      >
                        Snooze
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSkipRecord({ record: r, kind: 'skip' })}
                      >
                        Skip
                      </Button>
                    </>
                  )}
                </div>
              ),
              className: 'text-right',
            },
          ]}
        />
      )}

      {logRecord ? (
        <LogComplianceSheet
          record={logRecord}
          onClose={() => setLogRecord(null)}
          onSubmitted={() => {
            setLogRecord(null);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
          }}
        />
      ) : null}

      {skipRecord ? (
        <SkipSnoozeDialog
          kind={skipRecord.kind}
          record={skipRecord.record}
          onClose={() => setSkipRecord(null)}
          onSubmitted={() => {
            setSkipRecord(null);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
          }}
        />
      ) : null}
    </div>
  );
}

function StatusFilter({
  value,
  onChange,
}: {
  value: ComplianceRecordStatus[];
  onChange: (next: ComplianceRecordStatus[]) => void;
}) {
  function toggle(status: ComplianceRecordStatus) {
    const has = value.includes(status);
    onChange(has ? value.filter((s) => s !== status) : [...value, status]);
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Filter className="h-4 w-4 text-fg-muted" />
      {COMPLIANCE_RECORD_STATUSES.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => toggle(s)}
          className={cn(
            'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            value.includes(s)
              ? 'border-primary bg-primary-subtle text-primary'
              : 'border-border bg-surface text-fg-secondary hover:border-fg-muted',
          )}
        >
          {COMPLIANCE_RECORD_STATUS_LABELS[s]}
        </button>
      ))}
    </div>
  );
}

function parseList(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
